import * as React from 'react';
import {
  AutomationAction,
  AUTOMATION_ACTION_TYPE,
} from '../../../../MedicareProAPI/Entities/Automation/AutomationAction';
import {
  AUTOMATION_ACTION_TYPE_LABELS,
  USER_ACTIVITY_PRIORITY_TYPE_LABELS,
  THANKSIO_MAIL_TYPE_LABELS,
  THANKSIO_POSTCARD_SIZE_LABELS,
} from '../../../Language/en';
import { INDIVIDUAL_POLICY_STATUS_TYPE } from '../../../../MedicareProAPI/Entities/Ind/IndPolicy';
import { Utilities } from '../../../../MedicareProAPI/Utilities/Utilities';
import { WebAPI } from '../../../../MedicareProAPI/API/WebAPI';

import StyledModal from '../../../Components/StyledModal';

/**
 * Types
 */
export const THANKSIO_MAIL_TYPE = {
  POSTCARD: 'postcard',
  NOTECARD: 'notecard',
  LETTER: 'letter',
  WINDOWLESSLETTER: 'windowlessletter',
  GITFCARD: 'giftcard',
} as const;

export type THANKSIO_MAIL_TYPE = (typeof THANKSIO_MAIL_TYPE)[keyof typeof THANKSIO_MAIL_TYPE];

export const THANKSIO_POSTCARD_SIZE = {
  FOURBYSIX: '4x6',
  SIXBYNINE: '6x9',
} as const;

export type THANKSIO_POSTCARD_SIZE =
  (typeof THANKSIO_POSTCARD_SIZE)[keyof typeof THANKSIO_POSTCARD_SIZE];

export interface ThanksIOTemplateProps {
  messageTemplateId?: number;
  messageTemplateText?: string;
  imageTemplateId?: number;
  handwritingStyleId?: number;
  postcardSize?: THANKSIO_POSTCARD_SIZE;
  templateType?: THANKSIO_MAIL_TYPE;
  giftcardAmount?: number;
}

export interface ThanksIOTemplateSelectProps {
  userAccessToken: string;
  userId: number;

  saveButtonLabel?: string;
  onSave?: (templateData: ThanksIOTemplateProps) => void;

  templateData: ThanksIOTemplateProps;
}

export const ThanksIOTemplateSelect = (props: ThanksIOTemplateSelectProps): React.JSX.Element => {
  const [saveButtonLabel, setSaveButtonLabel] = React.useState<string>(
    props.saveButtonLabel || 'Save Changes'
  );

  const [templateData, setTemplateData] = React.useState<ThanksIOTemplateProps>(
    props.templateData || {}
  );
  const [templateLiveData, setTemplateLiveData] = React.useState<ThanksIOTemplateProps>(
    props.templateData || {}
  );
  const [templateLiveDataImageTemplate, setTemplateLiveDataImageTemplate] = React.useState<any>();

  const [messageTemplates, setMessageTemplates] = React.useState<any>();
  const [messageTemplatesLoading, setMessageTemplatesLoading] = React.useState<boolean>(true);
  const [messageTemplateCustomEnabled, setMessageTemplateCustomEnabled] = React.useState<boolean>(
    props.templateData?.messageTemplateText !== undefined
  );

  const [handwritingStyles, setHandwritingStyles] = React.useState<any>();
  const [handwritingStylesLoading, setHandwritingStylesLoading] = React.useState<boolean>(true);

  const [imageTemplates, setImageTemplates] = React.useState<any>();
  const [imageTemplatesLoading, setImageTemplatesLoading] = React.useState<boolean>(true);

  const [filteredMessageTemplates, setFilteredMessageTemplates] = React.useState<any>();
  const [filteredImageTemplates, setFilteredImageTemplates] = React.useState<any>();

  const [showModal, setShowModal] = React.useState<boolean>(false);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.userAccessToken);

  React.useEffect(() => {
    // Message tmeplates
    webAPI.getUserIntegrationThanksioMessageTemplates(props.userId).then((response) => {
      setMessageTemplates(response.data);
      setMessageTemplatesLoading(false);
    });

    // Image templates
    webAPI.getUserIntegrationThanksioImageTemplates(props.userId).then((response) => {
      setImageTemplates(response.data);
      setImageTemplatesLoading(false);
    });

    // Handwriting Styles
    webAPI.getUserIntegrationThanksioHandwritingStyles(props.userId).then((response) => {
      setHandwritingStyles(response.data);
      setHandwritingStylesLoading(false);
    });

    if (templateData.templateType === undefined) {
      handleTemplateTypeChange(THANKSIO_MAIL_TYPE.POSTCARD);
    }
  }, []);

  const displayModal = () => {
    setShowModal(true);
  };

  const hideModal = () => {
    setShowModal(false);
  };

  React.useEffect(() => {
    if (imageTemplatesLoading === false && messageTemplatesLoading === false) {
      let newFilteredImageTemplates = [];

      if (imageTemplates.length > 0) {
        newFilteredImageTemplates = imageTemplates.filter(
          (imageTemplate) => imageTemplate.type === templateData.templateType
        );
      }

      setFilteredImageTemplates(newFilteredImageTemplates);

      let newFilteredMessageTemplates = [];

      if (messageTemplates.length > 0) {
        newFilteredMessageTemplates = messageTemplates.filter(
          (messageTemplate) => messageTemplate.type === templateData.templateType
        );
      }

      setFilteredMessageTemplates(newFilteredMessageTemplates);
    }
  }, [templateData.templateType, imageTemplatesLoading, messageTemplatesLoading]);

  React.useEffect(() => {
    if (templateLiveData) {
      if (templateLiveData.imageTemplateId) {
        webAPI
          .getUserIntegrationThanksioImageTemplate(props.userId, templateLiveData.imageTemplateId)
          .then((templateResponse) => {
            setTemplateLiveDataImageTemplate(templateResponse);
          });
      }
    }
  }, [templateLiveData]);

  const handlePostcardSizeChange = (value?: THANKSIO_POSTCARD_SIZE) => {
    handleTemplateDataChange({ ...templateData, postcardSize: value });
  };

  const handleImageTemplateIdChange = (value?: number) => {
    handleTemplateDataChange({ ...templateData, imageTemplateId: value });
  };

  const handleMessageTemplateTextChange = (value?: string) => {
    handleTemplateDataChange({ ...templateData, messageTemplateText: value });
  };

  const handleMessageTemplateIdChange = (value?: number) => {
    handleTemplateDataChange({ ...templateData, messageTemplateId: value });
  };

  const handleHandwritingStyleIdChange = (value?: number) => {
    handleTemplateDataChange({ ...templateData, handwritingStyleId: value });
  };

  const handleTemplateTypeChange = (value?: THANKSIO_MAIL_TYPE) => {
    handleTemplateDataChange({ ...templateData, templateType: value });
  };

  const handleGiftcardAmountChange = (value?: number) => {
    handleTemplateDataChange({ ...templateData, giftcardAmount: value });
  };

  const handleTemplateDataChange = (newTemplateData: ThanksIOTemplateProps) => {
    setTemplateData(newTemplateData);
  };

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return (
    <>
      <StyledModal
        size="xl"
        title="Thanks.io"
        footerCloseButton={true}
        footerButtons={
          <div
            onClick={(e) => {
              if (props.onSave) {
                props.onSave(templateData);
                setTemplateLiveData({ ...templateData });
              }

              hideModal();
            }}
            className="btn btn-success"
          >
            {saveButtonLabel}
          </div>
        }
        show={showModal}
        onClose={hideModal}
      >
        <div className="row">
          <div className="col-12 col-lg-3 mb-2 border-right ">
            <div>
              {THANKSIO_MAIL_TYPE_LABELS.map((item) => {
                let icon;
                const selected = templateData.templateType === item.value;

                if (THANKSIO_MAIL_TYPE.NOTECARD === item.value) {
                  icon = <i className="fas fa-edit"></i>;
                } else if (THANKSIO_MAIL_TYPE.LETTER === item.value) {
                  icon = <i className="fal fa-envelope"></i>;
                } else if (THANKSIO_MAIL_TYPE.WINDOWLESSLETTER === item.value) {
                  icon = <i className="fas fa-envelope"></i>;
                } else if (THANKSIO_MAIL_TYPE.POSTCARD === item.value) {
                  icon = <i className="fas fa-mailbox"></i>;
                } else if (THANKSIO_MAIL_TYPE.GITFCARD === item.value) {
                  icon = <i className="far fa-gift-card"></i>;
                }

                const classList = [
                  'btn',
                  'btn-sm',
                  'border',
                  'border-secondary',
                  'mb-2',
                  'w-100',
                  'p-2',
                ];

                if (selected) {
                  classList.push('btn-info');
                }

                return (
                  <div
                    onClick={(e) => handleTemplateTypeChange(item.value)}
                    className={classList.join(' ')}
                  >
                    <div>{icon}</div>
                    <b>{item.label}</b>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="col-12 col-lg-9" /*style="max-height:450px; overflow-y:scroll;"*/>
            {templateData.templateType === THANKSIO_MAIL_TYPE.POSTCARD && (
              <div className="mt-3">
                <label className="label">
                  <b>Select a postcard size</b>
                </label>
                <div>
                  {THANKSIO_POSTCARD_SIZE_LABELS.map((item) => {
                    const selected = item.value === templateData.postcardSize;
                    const classList = ['badge', 'border', 'border-secondary', 'mr-2', 'cp'];

                    if (selected) {
                      classList.push('badge-info');
                    }

                    return (
                      <div
                        onClick={(e) => handlePostcardSizeChange(item.value)}
                        className={classList.join(' ')}
                      >
                        {item.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="mt-3">
              <label className="label">
                <b>Select an image template</b>
              </label>
              {imageTemplatesLoading === true && (
                <div className="alert small alert-info" role="alert">
                  <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
                </div>
              )}
              {imageTemplatesLoading === false &&
                filteredImageTemplates &&
                filteredImageTemplates.length === 0 && (
                  <div className="alert small alert-info" role="alert">
                    No image templates to show
                  </div>
                )}
              {filteredImageTemplates && filteredImageTemplates.length > 0 && (
                <div className="row">
                  {filteredImageTemplates.map((template) => {
                    const templateCheckClassList = [
                      'fas',
                      'fa-check-circle',
                      'text-success',
                      'float-right',
                      'imageTemplateSelectedCheck',
                    ];
                    const selected = template.id === templateData.imageTemplateId;

                    if (!selected) {
                      templateCheckClassList.push('d-none');
                    }

                    return (
                      <div
                        onClick={(e) => handleImageTemplateIdChange(template.id)}
                        className="ml-3 col-12 col-lg-4 border border-secondary rounded mb-2 mt-2 text-center cp  p-2 thanksio-image-template"
                      >
                        <div className="font-weight-bold mb-2">
                          {template.name}
                          <i className={templateCheckClassList.join(' ')}></i>
                        </div>
                        <img src={template.image} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="mt-3">
              <label className="label">
                <b>Select a message template or write a new message</b>
              </label>
              {messageTemplatesLoading === true && (
                <div className="alert small alert-info" role="alert">
                  <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
                </div>
              )}
              {messageTemplatesLoading === false &&
                filteredMessageTemplates &&
                filteredMessageTemplates.length === 0 && (
                  <div className="alert small alert-info" role="alert">
                    No message templates to show
                  </div>
                )}
              {!messageTemplateCustomEnabled &&
                filteredMessageTemplates &&
                filteredMessageTemplates.length > 0 && (
                  <div className="row">
                    {filteredMessageTemplates.map((template) => {
                      const templateCheckClassList = [
                        'fas',
                        'fa-check-circle',
                        'text-success',
                        'float-right',
                        'messageTemplateSelectedCheck',
                      ];
                      const selected = template.id === templateData.messageTemplateId;

                      if (!selected) {
                        templateCheckClassList.push('d-none');
                      }

                      return (
                        <div
                          onClick={(e) => handleMessageTemplateIdChange(template.id)}
                          className="ml-3 col-12 col-lg-4 mb-3 mt-2 border border-secondary rounded text-center cp p-2 templateInstance thanksio-message-template"
                        >
                          <div className="template-name font-weight-bold mb-2">
                            {template.name}
                            <i className={templateCheckClassList.join(' ')}></i>
                          </div>
                          <div className="template-message">
                            {template.content.substring(0, 200)}...
                          </div>
                          {template.type === 'giftcard' && template.brand_data && (
                            <>
                              {template.brand_data.image && (
                                <img src={template.brand_data.image} className="giftcard-image" />
                              )}
                              {template.brand_data.title && (
                                <div className="giftcard-title font-weight-bold">
                                  {template.brand_data.title}
                                </div>
                              )}
                              {template.brand_data.available_amounts && (
                                <>
                                  <div className={selected ? '' : 'opacity-25'}>
                                    <div className="mt-3 mb-2 label giftcard-amount-label">
                                      <b>Select giftcard amount</b>
                                    </div>
                                    {template.brand_data.available_amounts.map((amount) => {
                                      const roundedAmount = amount / 100;
                                      const classList = [
                                        'badge',
                                        'border',
                                        'border-success',
                                        'mr-2',
                                        'cp',
                                        'giftcard-amount-button',
                                      ];

                                      if (templateData.giftcardAmount === amount) {
                                        classList.push('badge-success');
                                      }

                                      return (
                                        <div
                                          onClick={(e) => {
                                            if (selected) {
                                              e.stopPropagation();
                                              handleGiftcardAmountChange(amount);
                                            }
                                          }}
                                          className={classList.join(' ')}
                                        >
                                          {formatter.format(roundedAmount)}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              {templateData.templateType !== 'giftcard' && (
                <>
                  {messageTemplateCustomEnabled && (
                    <>
                      <div>
                        <textarea
                          className="form-control mb-3"
                          onChange={(e) => handleMessageTemplateTextChange(e.target.value)}
                        >
                          {templateData.messageTemplateText}
                        </textarea>
                      </div>
                      <div className="mt-3">
                        <label className="label">
                          <b>Select a handwriting style</b>
                        </label>
                        {handwritingStylesLoading === true && (
                          <div className="alert small alert-info" role="alert">
                            <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
                          </div>
                        )}
                        {handwritingStylesLoading === false &&
                          handwritingStyles &&
                          handwritingStyles.length === 0 && (
                            <div className="alert small alert-info" role="alert">
                              No image templates to show
                            </div>
                          )}
                        {handwritingStyles && handwritingStyles.length > 0 && (
                          <select
                            className="form-control form-control-sm"
                            onChange={(e) =>
                              handleHandwritingStyleIdChange(
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                          >
                            <option value={null}></option>
                            {handwritingStyles.map((template) => {
                              const handwritingStyleId = parseInt(template.handwriting_style_id);
                              return (
                                <option
                                  selected={handwritingStyleId === templateData.handwritingStyleId}
                                  value={handwritingStyleId}
                                >
                                  {template.name}
                                </option>
                              );
                            })}
                          </select>
                        )}
                      </div>
                      <div
                        className="btn border border-danger mt-3"
                        onClick={(e) => {
                          handleMessageTemplateTextChange(undefined);
                          setMessageTemplateCustomEnabled(false);
                        }}
                      >
                        <i className="far fa-minus-circle"></i> Remove custom message
                      </div>
                    </>
                  )}
                  {!messageTemplateCustomEnabled && (
                    <>
                      <div
                        className="btn border border-info"
                        onClick={(e) => setMessageTemplateCustomEnabled(true)}
                      >
                        <i className="far fa-plus-circle"></i> Write custom message
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </StyledModal>

      <div>
        {templateLiveDataImageTemplate && (
          <img className="template-preview-image" src={templateLiveDataImageTemplate.image} />
        )}
        <div onClick={displayModal} className="btn btn-sm border border-primary">
          {templateLiveDataImageTemplate ? 'Change Template' : 'Choose Template'}
        </div>
      </div>

      <small>
        <i className="far fa-question-circle mr-1 "></i>What template do you want to use?
      </small>
    </>
  );
};
